<template>
  <section class="certification-change-notes-list">
    <div v-if="loading" class="certification-change-notes-list__loading">
      <span
        class="certification-change-notes-list__spinner"
        aria-hidden="true"
      />
      Loading change notes...
    </div>
    <p v-else-if="error" class="certification-change-notes-list__error">
      {{ error }}
    </p>
    <p v-else-if="!notes.length" class="certification-change-notes-list__empty">
      No change notes yet.
    </p>
    <ul
      v-else
      class="certification-change-notes-list__items"
      @scroll="onScroll"
    >
      <li
        v-for="note in notes"
        :key="note.id"
        class="certification-change-notes-list__item"
      >
        <div class="certification-change-notes-list__metadata">
          <strong>{{ humanize(note.event_type) }}</strong>
          <span>{{ note.entity_label || humanize(note.entity_type) }}</span>
          <span>{{ note.created_at | dateFormat }}</span>
        </div>
        <p class="certification-change-notes-list__note">
          Note: {{ note.note }}
        </p>
        <ul
          v-if="changesOf(note).length"
          class="certification-change-notes-list__changes"
        >
          <li v-for="change in changesOf(note)" :key="change.field">
            <strong>{{ formatField(change.field) }}</strong>
            <pre
              v-if="change.diff"
              class="certification-change-notes-list__diff"
            ><span
                v-for="(line, index) in change.diff"
                :key="index"
                :class="diffLineClass(line)"
              >{{ line }}
</span></pre>
            <span v-else-if="change.changed">changed</span>
            <span v-else class="certification-change-notes-list__value-change">
              <span class="certification-change-notes-list__value certification-change-notes-list__value--removed">
                {{ formatValue(change.from) }}
              </span>
              <span class="certification-change-notes-list__value-arrow" aria-hidden="true">
                &rarr;
              </span>
              <span class="certification-change-notes-list__value certification-change-notes-list__value--added">
                {{ formatValue(change.to) }}
              </span>
            </span>
          </li>
        </ul>
        <small v-if="note.author_name || note.author_id">
          Author:
          <a
            v-if="note.author_id && supportURL"
            :href="`${supportURL}/users/${note.author_id}`"
            target="_blank"
            rel="noopener noreferrer"
          >{{ note.author_name || note.author_id }}</a>
          <template v-else>{{ note.author_name || note.author_id }}</template>
        </small>
      </li>
      <li v-if="loadingMore" class="certification-change-notes-list__more">
        Loading more...
      </li>
      <li
        v-else-if="loadMoreError"
        class="certification-change-notes-list__more certification-change-notes-list__more--error"
      >
        {{ loadMoreError }}
        <button type="button" @click="$emit('load-more')">Retry</button>
      </li>
    </ul>
  </section>
</template>

<script>
export default {
  name: 'CertificationChangeNotesList',
  props: {
    notes: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    loadMoreError: {
      type: String,
      default: '',
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    supportURL: (process.env.VUE_APP_SUPPORT_URL || '').replace(/\/$/, ''),
  }),
  methods: {
    onScroll(event) {
      if (!this.hasMore || this.loadingMore) return
      const el = event.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        this.$emit('load-more')
      }
    },
    changesOf(note) {
      const changes = (note.metadata && note.metadata.changes) || {}
      return Object.keys(changes).map((field) => ({
        field,
        ...changes[field],
      }))
    },
    diffLineClass(line) {
      const prefix = 'certification-change-notes-list__diff-line'
      if (line.startsWith('@@')) return `${prefix}--hunk`
      if (line.startsWith('+')) return `${prefix}--added`
      if (line.startsWith('-')) return `${prefix}--removed`
      return prefix
    },
    formatField(field) {
      return field
        .split('.')
        .map((part) => this.humanize(part))
        .join(' › ')
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') return '—'
      return typeof value === 'object' ? JSON.stringify(value) : String(value)
    },
    humanize(value) {
      if (!value) return ''
      return value.replace(/_/g, ' ')
    },
  },
}
</script>

<style scoped>
.certification-change-notes-list__loading,
.certification-change-notes-list__error,
.certification-change-notes-list__empty {
  color: #626262;
  margin: 0;
}
.certification-change-notes-list__error {
  color: #ea5455;
}
.certification-change-notes-list__spinner {
  animation: certification-change-notes-spin 0.8s linear infinite;
  border: 2px solid #d8d8d8;
  border-radius: 50%;
  border-top-color: rgba(var(--vs-primary), 1);
  display: inline-block;
  height: 14px;
  margin-right: 6px;
  vertical-align: -2px;
  width: 14px;
}
.certification-change-notes-list__items {
  list-style: none;
  margin: 0;
  max-height: 480px;
  overflow-y: auto;
  padding: 0;
}
.certification-change-notes-list__more {
  color: #626262;
  padding: 8px 0;
  text-align: center;
}
.certification-change-notes-list__item {
  border-bottom: 1px solid #ececec;
  padding: 12px 0;
}
.certification-change-notes-list__item:last-child {
  border-bottom: 0;
}
.certification-change-notes-list__metadata {
  color: #626262;
  display: flex;
  font-size: 0.8rem;
  gap: 10px;
}
.certification-change-notes-list__metadata strong {
  color: #2c2c2c;
  text-transform: capitalize;
}
.certification-change-notes-list__note {
  margin: 6px 0;
  white-space: pre-wrap;
}
.certification-change-notes-list__changes {
  color: #626262;
  font-size: 0.8rem;
  list-style: none;
  margin: 0 0 6px;
  padding: 0;
}
.certification-change-notes-list__changes strong {
  margin-right: 6px;
  text-transform: capitalize;
}
.certification-change-notes-list__value-change {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
}
.certification-change-notes-list__value {
  border-radius: 3px;
  padding: 1px 4px;
  white-space: pre-wrap;
  word-break: break-word;
}
.certification-change-notes-list__value--removed {
  background: #fceeee;
  color: #b31d28;
}
.certification-change-notes-list__value--added {
  background: #daf6e2;
  color: #22863a;
}
.certification-change-notes-list__value-arrow {
  align-self: center;
}
.certification-change-notes-list__diff {
  background: #f8f8f8;
  border-radius: 4px;
  font-size: 0.75rem;
  margin: 4px 0;
  overflow-x: auto;
  padding: 6px 8px;
}
.certification-change-notes-list__diff-line--added {
  background: #daf6e2;
  color: #22863a;
}
.certification-change-notes-list__diff-line--removed {
  background: #f3d7da;
  color: #b31d28;
}
.certification-change-notes-list__diff-line--hunk {
  color: #6f42c1;
}
@keyframes certification-change-notes-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
